
























import { mixins } from "vue-class-component";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  ContentFeedTypeEnum,
  InputContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithContentFeed from "@/mixins/WithContentFeed";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { userPublicDataFieldsWithStoryInfo } from "@/lib/fieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import { resolveUserIdByRoute } from "@/lib/userHelpers";
import config from "@/config";
import { ViewStoriesSettings } from "@/types";
import { EVENTS } from "@/const";
import { useAgeVerification } from "@/features/age-verification";
import PageProfileView from "./components/PageProfileView";

@Component({
  name: "PageProfile",
  components: { PageProfileView },
  setup() {
    const { onVerified } = useAgeVerification();
    return {
      onVerified,
    };
  },
})
export default class PageProfile extends mixins(WithContentFeed, WithAuthUser) {
  @Prop({ type: String, required: true })
  public readonly userId!: string;

  @Prop({ type: String })
  public readonly feedId!: string;

  @Prop({ type: String })
  public readonly storyId!: string;

  public onVerified!: ReturnType<typeof useAgeVerification>["onVerified"];

  public loading: boolean = true;

  private user: UserPublicData | null = null;

  private questionaryInfo: Record<string, any> = {};

  private contentFeedSearchResult: ContentFeedSearchResult | null = null;

  public subscriptionLevels: SubscriptionLevel[] = [];

  public paymentPackages: PaymentPackage[] = [];

  public scroll: boolean = false;

  public isFollowing: boolean = false;

  public showUpdateInfo: boolean = false;

  public get canUpdateContentSet() {
    return checkAuthorities(this.authUserAuthorities, this.isAuthor ? ["createMyContentSet"] : ["createAnyContentSet"]);
  }

  public get isAuthor() {
    return !!this.authUser && this.authUser.id === this.userId;
  }

  public onFeedClick(feedId: string) {
    if (this.user) this.$router.push(getProfileUrl(this.user, { feedId }));
  }

  public onCloseUpdateInfo() {
    this.showUpdateInfo = false;
  }

  public async onFollowHandler(status: boolean) {
    if (this.isFollowing !== status) {
      this.isFollowing = status;
      await this.getContentFeed(this.userId, {}, true, this.feedId || undefined);
    }
  }

  public onContentFeedUpdate(contentFeed: ContentFeed) {
    if (!this.contentFeedSearchResult) return;
    this.$set(
      this.contentFeedSearchResult,
      "result",
      this.contentFeedSearchResult.result.map(item => (item.id === contentFeed.id ? contentFeed : item))
    );
  }

  public onFeedDelete(feedId: string) {
    if (!this.contentFeedSearchResult) return;
    this.$set(
      this.contentFeedSearchResult,
      "result",
      this.contentFeedSearchResult.result.filter(item => item.id !== feedId)
    );
  }

  public async onNext(cb?: Function) {
    try {
      if (this.contentFeedSearchResult && this.contentFeedSearchResult.hasNextPage) {
        await this.getContentFeed(this.userId, { page: this.contentFeedSearchResult.page + 1 }, false);
      }
    } finally {
      cb && cb();
    }
  }

  public async getContentFeed(
    userId: string,
    data?: Partial<InputContentFeedSearch>,
    replace: boolean = true,
    feedIdToFind?: string
  ) {
    try {
      this.scroll = false;
      let response = await this.contentFeedSearch(
        {
          userId: userId,
          sort: "PUBLISH_TIME,DESC",
          processingStatus: this.canUpdateContentSet ? undefined : "OK",
          published: true,
          includeScheduled: this.canUpdateContentSet,
          types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
          ...data,
        },
        feedIdToFind
      );
      if (replace) {
        this.contentFeedSearchResult = response;
      } else {
        this.contentFeedSearchResult = {
          ...response,
          result: [...(this.contentFeedSearchResult ? this.contentFeedSearchResult.result : []), ...response.result],
        };
      }
      return this.contentFeedSearchResult;
    } finally {
      this.scroll = this.contentFeedSearchResult?.hasNextPage ?? false;
    }
  }

  public async getFollowingData(authUserId: string, profileUserId: string) {
    await providers.FollowingsProvider.searchFollowings({ ids: [profileUserId] }).then(r => {
      this.isFollowing = !!r.find(i => i.followerId === authUserId && i.userId === profileUserId);
    });
  }

  @Watch("userId")
  public async getProfileData(userId: string, oldUserId?: string) {
    this.loading = true;
    this.user = null as UserPublicData | null;
    this.questionaryInfo = {};
    this.contentFeedSearchResult = null;
    const promises: Promise<any>[] = [
      providers.UserPublicDataProvider.search({ data: { id: [userId] } }, userPublicDataFieldsWithStoryInfo()).then(
        r => {
          if (r.result[0]) this.user = r.result[0];
        }
      ),
      providers.QuestionaryInfoProvider.find({ id: userId }).then(userQuestionaryInfo => {
        this.questionaryInfo = userQuestionaryInfo.fields;
      }),
      this.getContentFeed(userId, {}, true, this.feedId || undefined),
      providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
        r => (this.paymentPackages = r)
      ),
    ];
    if (this.authUser && !this.isAuthor) {
      promises.push(this.getFollowingData(this.authUser.id, userId));
    }
    if (this.isAuthor && this.authUser?.role !== "member") {
      providers.PayeeInformationProvider.find({ id: userId }).then(r => {
        if (r.payoutSystem !== "masspay") this.showUpdateInfo = true;
      });
    }
    await Promise.all(promises).finally(() => {
      this.loading = false;
      if (this.user?.verified) {
        providers.SubscriptionLevelProvider.find({ userIds: [userId] }, [
          "id",
          "userId",
          "pricePackageId",
          { pricePackage: ["amount", { costs: ["cost", "currency"] }] },
          "contentCount",
        ]).then(res => (this.subscriptionLevels = res));
      }
      if (oldUserId && oldUserId !== userId) {
        window.scrollTo({ top: 0 });
      }
    });
  }

  @Watch("authUser")
  public updateDataForLogin(newAuthUser: User | null, oldAuthUser: User | null) {
    if (newAuthUser && !this.isAuthor) {
      this.getFollowingData(newAuthUser.id, this.userId);
    }
    if (!oldAuthUser && newAuthUser && newAuthUser.accountInfo.lastLoginTime && this.authAllowAccess) {
      const resultData = this.contentFeedSearchResult?.result ?? [];
      const userId = resultData.find(() => true)?.user.id;
      if (userId) {
        this.updateFeed({ userId });
      }
    }
  }

  public async updateFeed(payload: { userId: string; feedId?: string }) {
    if (!this.contentFeedSearchResult) {
      return this.getContentFeed(this.userId, {}, true);
    }

    this.$set(
      this.contentFeedSearchResult,
      "result",
      await getPurchasedContentFeeds(
        { authorId: payload.userId, purchasedFeedId: payload.feedId },
        this.contentFeedSearchResult,
        { includeScheduled: this.canUpdateContentSet }
      )
    );
  }

  public showStories() {
    if (!config.featureStories) return;
    if (this.storyId && this.user)
      if (checkRole(this.user?.role ?? "", "creator")) {
        emitter.emit<ViewStoriesSettings>(EVENTS.STORIES_SHOW, {
          storyPublishers: [{ userId: this.userId, user: this.user }],
          currentStoryId: this.storyId,
          back: getProfileUrl(this.user),
          routeSupport: true,
        });
      } else {
        if (this.user) this.$router.replace(getProfileUrl(this.user));
      }
  }

  public created() {
    this.getProfileData(this.userId).then(() => this.showStories());

    emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, this.updateFeed);
    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, this.updateFeed);

    this.onVerified(() => this.getProfileData(this.userId));
  }

  public destroyed() {
    emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, this.updateFeed);
    emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, this.updateFeed);
  }

  public beforeRouteEnter(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  }

  public beforeRouteUpdate(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  }
}
